import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { clearAll as _clearAllCaches, readCache, writeCache, readCacheMeta } from '../utils/localCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DatasetContext = createContext();

export function DatasetProvider({ children }) {
  const { getAuthHeaders } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [geocodingStats, setGeocodingStats] = useState({});

  // Global display time range (from app settings)
  const [displayTimeRange, setDisplayTimeRange] = useState({ start: 2020, end: 2025 });

  // Debounced selected IDs — batches rapid selection changes into one update
  const [debouncedSelectedIds, setDebouncedSelectedIds] = useState('');
  const debounceTimer = useRef(null);
  const prevSelectedCount = useRef(0);

  useEffect(() => {
    const newIds = selectedDatasets.length > 0 ? selectedDatasets.join(',') : '';
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    // Use shorter debounce for single toggle, longer for bulk (Select All)
    const countDelta = Math.abs(selectedDatasets.length - prevSelectedCount.current);
    prevSelectedCount.current = selectedDatasets.length;
    const delay = countDelta <= 1 ? 50 : 300;
    debounceTimer.current = setTimeout(() => {
      setDebouncedSelectedIds(newIds);
    }, delay);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [selectedDatasets]);

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...getAuthHeaders(), ...options.headers };
    return fetch(url, { ...options, headers });
  }, [getAuthHeaders]);

  const fetchDatasets = useCallback(async (retryCount = 0) => {
    try {
      setFetchError(false);
      const [dsRes, folderRes] = await Promise.all([
        authFetch(`${API}/datasets`),
        authFetch(`${API}/folders`),
      ]);
      if (!dsRes.ok || !folderRes.ok) throw new Error('Failed');
      const dsData = await dsRes.json();
      const folderData = await folderRes.json();
      setDatasets(dsData);
      setFolders(folderData);
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
      setFetchError(true);
      if (retryCount < 3) {
        setTimeout(() => fetchDatasets(retryCount + 1), (retryCount + 1) * 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { fetchDatasets(); }, [fetchDatasets]);

  const fetchGeocodingStats = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/datasets/geocoding-stats`);
      if (res.ok) {
        const data = await res.json();
        setGeocodingStats(data);
      }
    } catch (e) {
      // Silently fail — stats are non-critical
    }
  }, [authFetch]);

  // Fetch geocoding stats on mount and every 60s (data changes infrequently).
  // Also re-fetch shortly after the dataset list changes so a newly uploaded
  // dataset picks up its badge without waiting for the next 60s tick.
  useEffect(() => {
    fetchGeocodingStats();
    const interval = setInterval(fetchGeocodingStats, 60000);
    return () => clearInterval(interval);
  }, [fetchGeocodingStats]);

  const datasetIdSig = datasets.map(d => d.id).sort().join(',');
  useEffect(() => {
    if (!datasetIdSig) return;
    // Short delay lets the backend's post-upload `_bg_geostats` task land
    // before we poll — otherwise we'd fetch too early and still miss it.
    const t = setTimeout(fetchGeocodingStats, 3000);
    return () => clearTimeout(t);
  }, [datasetIdSig, fetchGeocodingStats]);

  // Fetch app settings (display time range) on mount
  const fetchAppSettings = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/settings/app`);
      if (res.ok) {
        const data = await res.json();
        setDisplayTimeRange({ start: data.display_year_start, end: data.display_year_end });
      }
    } catch (e) { /* non-critical */ }
  }, [authFetch]);

  useEffect(() => { fetchAppSettings(); }, [fetchAppSettings]);

  const toggleDataset = (datasetId) => {
    setSelectedDatasets(prev =>
      prev.includes(datasetId) ? prev.filter(id => id !== datasetId) : [...prev, datasetId]
    );
  };

  const selectAll = () => setSelectedDatasets(datasets.map(d => d.id));
  const selectNone = () => setSelectedDatasets([]);

  const toggleFolder = (folderId) => {
    const folderDatasets = datasets.filter(d => d.folder_id === folderId).map(d => d.id);
    const allSelected = folderDatasets.every(id => selectedDatasets.includes(id));
    if (allSelected) {
      setSelectedDatasets(prev => prev.filter(id => !folderDatasets.includes(id)));
    } else {
      setSelectedDatasets(prev => [...new Set([...prev, ...folderDatasets])]);
    }
  };

  // When the dataset list signature changes (new dataset arrived after
  // upload, or any dataset disappeared), every cached aggregation can be
  // stale. Wipe localStorage so the next page paint refetches from the
  // backend. We skip the very first signature transition (empty → loaded)
  // so a regular page load doesn't blow away the cache it just read.
  const lastDatasetSigRef = useRef(null);
  useEffect(() => {
    const sig = datasets.map(d => d.id).sort().join(',');
    if (lastDatasetSigRef.current === null) {
      lastDatasetSigRef.current = sig;
      return;
    }
    if (sig !== lastDatasetSigRef.current) {
      lastDatasetSigRef.current = sig;
      _clearAllCaches();
    }
  }, [datasets]);

  const updateDatasetName = async (datasetId, name) => {
    try {
      await authFetch(`${API}/datasets/${datasetId}?name=${encodeURIComponent(name)}`, { method: 'PATCH' });
      setDatasets(prev => prev.map(d => d.id === datasetId ? { ...d, name } : d));
    } catch (error) { console.error('Failed to update dataset name:', error); }
  };

  const deleteDataset = async (datasetId) => {
    try {
      await authFetch(`${API}/datasets/${datasetId}`, { method: 'DELETE' });
      setDatasets(prev => prev.filter(d => d.id !== datasetId));
      setSelectedDatasets(prev => prev.filter(id => id !== datasetId));
      // Donations from this dataset just disappeared — every cached
      // aggregation that included it is now stale.
      _clearAllCaches();
    } catch (error) { console.error('Failed to delete dataset:', error); }
  };

  const moveDatasetToFolder = async (datasetId, folderId) => {
    // Snapshot the current folder assignment so we can roll back if the
    // backend rejects the move. `authFetch` is a thin `fetch` wrapper — it
    // does NOT throw on 4xx/5xx, so we must explicitly check `res.ok`,
    // otherwise a 403 would silently leave the optimistic UI in a bad
    // state until the next page reload.
    const prev = datasets.find(d => d.id === datasetId);
    const prevFolderId = prev ? prev.folder_id : null;
    setDatasets(list => list.map(d =>
      d.id === datasetId ? { ...d, folder_id: folderId || null } : d
    ));
    try {
      const params = folderId ? `folder_id=${folderId}` : `clear_folder=true`;
      const res = await authFetch(`${API}/datasets/${datasetId}?${params}`, { method: 'PATCH' });
      if (!res.ok) {
        const msg = (await res.json().catch(() => ({}))).detail || `Move failed (${res.status})`;
        // Roll back optimistic update
        setDatasets(list => list.map(d =>
          d.id === datasetId ? { ...d, folder_id: prevFolderId } : d
        ));
        try {
          const { toast } = await import('sonner');
          toast.error(msg);
        } catch (e) { console.error(msg); }
      }
    } catch (error) {
      setDatasets(list => list.map(d =>
        d.id === datasetId ? { ...d, folder_id: prevFolderId } : d
      ));
      console.error('Failed to move dataset:', error);
    }
  };

  const createFolder = async (name) => {
    try {
      const res = await authFetch(`${API}/folders?name=${encodeURIComponent(name)}`, { method: 'POST' });
      const data = await res.json();
      setFolders(prev => [...prev, data]);
      return data;
    } catch (error) { console.error('Failed to create folder:', error); return null; }
  };

  const renameFolder = async (folderId, name) => {
    try {
      await authFetch(`${API}/folders/${folderId}?name=${encodeURIComponent(name)}`, { method: 'PATCH' });
      setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name } : f));
    } catch (error) { console.error('Failed to rename folder:', error); }
  };

  const deleteFolder = async (folderId) => {
    try {
      await authFetch(`${API}/folders/${folderId}`, { method: 'DELETE' });
      setFolders(prev => prev.filter(f => f.id !== folderId));
      setDatasets(prev => prev.map(d => d.folder_id === folderId ? { ...d, folder_id: null } : d));
    } catch (error) { console.error('Failed to delete folder:', error); }
  };

  const getSelectedIds = () => selectedDatasets.length > 0 ? selectedDatasets.join(',') : '';

  // ─── Background Prewarm ──────────────────────────────────────────
  // When the user toggles datasets in the sidebar, silently fire off
  // Dashboard + Map requests 800ms after they stop toggling. This warms
  // both the backend cache and the browser's localStorage cache so that
  // when they click into the Dashboard or Donor Map tab, data appears
  // instantly instead of triggering a 10-20s aggregation. Fire-and-
  // forget, no spinners, no UI — pure perceived-performance win.
  const prewarmTimer = useRef(null);
  const prewarmAbortRef = useRef(null);
  const lastPrewarmIdsRef = useRef('');
  useEffect(() => {
    if (prewarmTimer.current) clearTimeout(prewarmTimer.current);
    if (prewarmAbortRef.current) {
      try { prewarmAbortRef.current.abort(); } catch (_) { /* ignore */ }
      prewarmAbortRef.current = null;
    }
    const ids = selectedDatasets.length > 0 ? selectedDatasets.join(',') : '';
    if (!ids) return;

    prewarmTimer.current = setTimeout(async () => {
      // Skip if this same combo was prewarmed in the last 60s. Prevents
      // wasted network on navigation-triggered re-renders that happen to
      // arrive with the same selection.
      if (lastPrewarmIdsRef.current === ids) {
        const meta = readCacheMeta('dashboard', ids);
        if (meta && Date.now() - meta.ts < 60_000) return;
      }
      lastPrewarmIdsRef.current = ids;

      const controller = new AbortController();
      prewarmAbortRef.current = controller;
      const headers = { ...getAuthHeaders() };

      // Dashboard prewarm — skip if a fresh (<5min) localCache exists.
      const dashMeta = readCacheMeta('dashboard', ids);
      const dashFresh = dashMeta && Date.now() - dashMeta.ts < 5 * 60_000;
      if (!dashFresh) {
        try {
          const r = await fetch(`${API}/dashboard-data?dataset_ids=${ids}`, {
            signal: controller.signal, headers,
          });
          if (r.ok) {
            const data = await r.json();
            try { writeCache('dashboard', ids, data); } catch (_) { /* ignore */ }
          }
        } catch (_) { /* silent — this is a background warmup */ }
      }

      // Map prewarm — warm the backend's `map_clusters_cache` so the
      // hook's own fetch hits cache instantly. We don't persist result
      // client-side (the hook doesn't read localCache).
      try {
        await fetch(`${API}/charts/location-clusters?dataset_ids=${ids}`, {
          signal: controller.signal, headers,
        });
      } catch (_) { /* silent */ }
    }, 800);

    return () => {
      if (prewarmTimer.current) clearTimeout(prewarmTimer.current);
    };
  }, [selectedDatasets]);
  // ─────────────────────────────────────────────────────────────────

  const value = {
    datasets, folders, selectedDatasets, loading, fetchError, geocodingStats,
    debouncedSelectedIds, displayTimeRange,
    toggleDataset, selectAll, selectNone, toggleFolder,
    updateDatasetName, deleteDataset, moveDatasetToFolder,
    createFolder, renameFolder, deleteFolder,
    fetchDatasets, getSelectedIds, authFetch, fetchAppSettings,
  };

  return (
    <DatasetContext.Provider value={value}>
      {children}
    </DatasetContext.Provider>
  );
}

export function useDatasets() {
  const context = useContext(DatasetContext);
  if (!context) throw new Error('useDatasets must be used within a DatasetProvider');
  return context;
}
